import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal, EASE } from "../components/motion/Reveal";
import { LAWS, CATEGORIES } from "../data/laws";

const LawRow = ({ law, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: index * 0.05, ease: EASE }}
  >
    <Link
      to={`/laws/${law.slug}`}
      data-testid={`law-card-${law.slug}`}
      className="group relative grid grid-cols-[3.5rem_1fr] items-center gap-5 border-t hairline py-8 sm:grid-cols-[6rem_1fr_auto] sm:gap-8 lg:py-10"
    >
      <span className="absolute inset-0 -mx-5 origin-left scale-x-0 bg-white transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100 sm:-mx-8" />

      <span className="relative font-serif text-4xl leading-none text-terracotta sm:text-6xl">
        0{law.number}
      </span>

      <span className="relative min-w-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
          {law.hebrew} · {law.kind}
        </span>
        <span className="mt-2 block font-serif text-3xl leading-tight text-ink sm:text-4xl lg:text-5xl">
          {law.fullTitle}
        </span>
        <span className="mt-3 block max-w-2xl text-sm leading-relaxed text-ink-soft sm:text-base">
          {law.summary}
        </span>
        <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted sm:hidden">
          Read <ArrowRight size={12} />
        </span>
      </span>

      <span className="relative hidden flex-col items-center gap-3 self-center sm:flex">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border hairline text-ink transition-all duration-500 group-hover:border-terracotta group-hover:bg-terracotta group-hover:text-parchment">
          <ArrowUpRight size={18} strokeWidth={1.5} className="transition-transform duration-500 group-hover:rotate-45" />
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink-muted transition-colors duration-500 group-hover:text-terracotta">
          Read
        </span>
      </span>
    </Link>
  </motion.div>
);

export default function Laws() {
  const groups = CATEGORIES.filter((c) => c.id !== "all").map((c) => ({
    ...c,
    laws: LAWS.filter((l) => l.category === c.id),
  }));

  return (
    <div data-testid="laws-page">
      <PageHeader
        testId="laws-header"
        eyebrow="The Seven Laws · Sheva Mitzvot"
        lines={["Seven laws.", "One principle."]}
        lede={
          <>
            <span className="block font-serif text-2xl italic leading-snug text-ink sm:text-3xl">
              Loyalty — to oneself, to G-d, and to the world.
            </span>
            <span className="mt-5 block">
              Every one of the seven commandments is an expression of that single principle.
              Choose any one to read where it comes from, what it means, and how it is lived.
            </span>
          </>
        }
        aside={
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            data-testid="laws-instruction"
            className="mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-terracotta"
          >
            <ArrowDown size={14} className="animate-bounce" />
            Choose a law to begin reading
          </motion.p>
        }
      />

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        {groups.map((g, gi) => (
          <div key={g.id} data-testid={`laws-group-${g.id}`} className={gi === 0 ? "" : "mt-20"}>
            <Reveal className="mb-6">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[10px] tracking-[0.22em] text-ink-muted">
                  {String(gi + 1).padStart(2, "0")}
                </span>
                <h2 className="font-serif text-2xl italic text-ink sm:text-3xl">{g.label}</h2>
                <span className="hidden h-px flex-1 bg-ink/10 sm:block" />
                <span className="hidden text-xs text-ink-muted sm:block">
                  {g.laws.length} {g.laws.length === 1 ? "law" : "laws"}
                </span>
              </div>
              <p className="mt-2 pl-8 text-sm text-ink-muted">{g.note}</p>
            </Reveal>
            {g.laws.map((law, i) => (
              <LawRow key={law.slug} law={law} index={i} />
            ))}
            <div className="border-t hairline" />
          </div>
        ))}

        <Reveal className="mt-24 flex flex-col gap-8 bg-ink px-8 py-12 text-parchment sm:px-12 sm:py-16 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="eyebrow">Not sure where to start?</p>
            <h2 className="mt-4 max-w-xl font-serif text-3xl leading-[1.1] tracking-tight sm:text-4xl">
              Begin with the first law. <span className="italic text-parchment/60">Everything else rests on it.</span>
            </h2>
          </div>
          <Link
            to={`/laws/${LAWS[0].slug}`}
            data-testid="laws-start-first"
            className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-parchment px-7 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:bg-terracotta hover:text-parchment"
          >
            Start with 01 · Idolatry
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
